import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Alert } from './ui/alert';
import {
  Search,
  MessageCircle,
  User,
  Loader2,
  Clock,
  Users,
} from 'lucide-react';
import { adminService, ChatSession, ChatMessage } from '../services/admin';
import { ExternalUser } from '../services/auth';

const ChatHistory: React.FC = () => {
  const [externalUsers, setExternalUsers] = useState<ExternalUser[]>([]);
  const [selectedUser, setSelectedUser] = useState<ExternalUser | null>(null);
  const [sessions, setSessions] = useState<ChatSession[]>([]);
  const [filteredSessions, setFilteredSessions] = useState<ChatSession[]>([]);
  const [selectedSession, setSelectedSession] = useState<ChatSession | null>(
    null
  );
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingSessions, setLoadingSessions] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [userSearchTerm, setUserSearchTerm] = useState('');
  const [sessionSearchTerm, setSessionSearchTerm] = useState('');

  useEffect(() => {
    const fetchExternalUsers = async () => {
      try {
        const data = await adminService.getAllUsers();
        setExternalUsers(data);
      } catch (err: any) {
        setError(
          err.response?.data?.message || 'Failed to fetch external users'
        );
      } finally {
        setLoading(false);
      }
    };

    fetchExternalUsers();
  }, []);

  useEffect(() => {
    if (!selectedUser) {
      setSessions([]);
      setFilteredSessions([]);
      setSelectedSession(null);
      setMessages([]);
      return;
    }

    const fetchSessionsForUser = async () => {
      try {
        setLoadingSessions(true);
        const data = await adminService.getChatSessionsByUserId(
          selectedUser.id
        );
        setSessions(data);
        setFilteredSessions(data);
        setSelectedSession(null);
        setMessages([]);
      } catch (err: any) {
        setError(
          err.response?.data?.message || 'Failed to fetch chat sessions'
        );
      } finally {
        setLoadingSessions(false);
      }
    };

    fetchSessionsForUser();
  }, [selectedUser]);

  useEffect(() => {
    const filtered = sessions.filter(session =>
      session.title?.toLowerCase().includes(sessionSearchTerm.toLowerCase())
    );
    setFilteredSessions(filtered);
  }, [sessionSearchTerm, sessions]);

  const handleUserSelect = (user: ExternalUser) => {
    setSelectedUser(user);
  };

  const handleViewMessages = async (session: ChatSession) => {
    try {
      setSelectedSession(session);
      const data = await adminService.getChatSessionMessages(session.id);
      setMessages(data);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch messages');
    }
  };

  const getBadgeColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800';
      case 'completed':
        return 'bg-blue-100 text-blue-800';
      case 'archived':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const filteredUsers = externalUsers
    .filter(
      user =>
        user.firstName?.toLowerCase().includes(userSearchTerm.toLowerCase()) ||
        user.lastName?.toLowerCase().includes(userSearchTerm.toLowerCase()) ||
        user.email?.toLowerCase().includes(userSearchTerm.toLowerCase())
    )
    .sort((a, b) => {
      // Create full name for sorting, fallback to email if no name
      const nameA =
        a.firstName && a.lastName
          ? `${a.firstName} ${a.lastName}`.toLowerCase()
          : a.email.toLowerCase();
      const nameB =
        b.firstName && b.lastName
          ? `${b.firstName} ${b.lastName}`.toLowerCase()
          : b.email.toLowerCase();

      return nameA.localeCompare(nameB);
    });

  if (loading) {
    return (
      <div className='flex justify-center items-center h-64'>
        <Loader2 className='h-8 w-8 animate-spin' />
      </div>
    );
  }

  return (
    <div className='space-y-4 sm:space-y-6'>
      <div className='flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4'>
        <div>
          <h1 className='text-2xl sm:text-3xl font-bold text-gray-900'>
            Chat History
          </h1>
          <p className='text-sm sm:text-base text-gray-600'>
            View and manage user chat sessions
          </p>
        </div>
      </div>

      {error && <Alert variant='destructive'>{error}</Alert>}

      {/* Stats Cards */}
      {/* <div className='grid grid-cols-1 md:grid-cols-3 gap-4'>
        <Card>
          <CardContent className='p-6'>
            <div className='text-center'>
              <h3 className='text-lg font-semibold text-gray-900'>
                Total Sessions
              </h3>
              <p className='text-3xl font-bold text-blue-600'>
                {sessions.length}
              </p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className='p-6'>
            <div className='text-center'>
              <h3 className='text-lg font-semibold text-gray-900'>
                Active Sessions
              </h3>
              <p className='text-3xl font-bold text-green-600'>
                {sessions.filter(s => s.status === 'active').length}
              </p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className='p-6'>
            <div className='text-center'>
              <h3 className='text-lg font-semibold text-gray-900'>
                Completed Sessions
              </h3>
              <p className='text-3xl font-bold text-purple-600'>
                {sessions.filter(s => s.status === 'completed').length}
              </p>
            </div>
          </CardContent>
        </Card>
      </div> */}

      <div className='grid grid-cols-1 lg:grid-cols-3 gap-6'>
        {/* External Users List */}
        <Card>
          <CardHeader>
            <CardTitle className='flex items-center gap-2'>
              <Users className='w-5 h-5' />
              Users
            </CardTitle>
            <div className='relative'>
              <Search className='absolute left-3 top-3 h-4 w-4 text-gray-400' />
              <Input
                placeholder='Search users...'
                value={userSearchTerm}
                onChange={e => setUserSearchTerm(e.target.value)}
                className='pl-10'
              />
            </div>
          </CardHeader>

          <CardContent>
            <div className='space-y-3 max-h-96 overflow-y-auto'>
              {filteredUsers.map(user => (
                <div
                  key={user.id}
                  className={`p-3 border rounded-lg cursor-pointer transition-colors ${
                    selectedUser?.id === user.id
                      ? 'border-blue-500 bg-blue-50'
                      : 'hover:bg-gray-50'
                  }`}
                  onClick={() => handleUserSelect(user)}
                >
                  <div className='flex items-center justify-between mb-1'>
                    <div className='flex items-center gap-2'>
                      <User className='w-4 h-4' />
                      <span className='font-medium'>
                        {user.firstName} {user.lastName}
                      </span>
                    </div>
                    {user.sessionCount !== undefined && (
                      <span className='px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded-full'>
                        {user.sessionCount}{' '}
                        {user.sessionCount === 1 ? 'session' : 'sessions'}
                      </span>
                    )}
                  </div>
                  <p className='text-sm text-gray-600'>{user.email}</p>
                </div>
              ))}

              {filteredUsers.length === 0 && (
                <div className='text-center py-8 text-gray-500'>
                  No external users found.
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Sessions List */}
        <Card>
          <CardHeader>
            <CardTitle className='flex items-center gap-2'>
              <MessageCircle className='w-5 h-5' />
              Chat Sessions
              {selectedUser && (
                <span className='text-sm font-normal text-gray-500'>
                  - {selectedUser.firstName} {selectedUser.lastName}
                </span>
              )}
            </CardTitle>
            {selectedUser && (
              <div className='relative'>
                <Search className='absolute left-3 top-3 h-4 w-4 text-gray-400' />
                <Input
                  placeholder='Search sessions...'
                  value={sessionSearchTerm}
                  onChange={e => setSessionSearchTerm(e.target.value)}
                  className='pl-10'
                />
              </div>
            )}
          </CardHeader>

          <CardContent>
            {selectedUser ? (
              loadingSessions ? (
                <div className='flex justify-center items-center h-32'>
                  <Loader2 className='h-6 w-6 animate-spin' />
                </div>
              ) : (
                <div className='space-y-3 max-h-96 overflow-y-auto'>
                  {filteredSessions.map(session => (
                    <div
                      key={session.id}
                      className={`p-4 border rounded-lg cursor-pointer transition-colors ${
                        selectedSession?.id === session.id
                          ? 'border-blue-500 bg-blue-50'
                          : 'hover:bg-gray-50'
                      }`}
                      onClick={() => handleViewMessages(session)}
                    >
                      <div className='flex items-start justify-between mb-2'>
                        <h3 className='font-medium text-gray-900 truncate'>
                          {session.title || 'Untitled Session'}
                        </h3>
                        <span
                          className={`px-2 py-1 text-xs rounded-full ${getBadgeColor(session.status || 'unknown')}`}
                        >
                          {session.status || 'unknown'}
                        </span>
                      </div>

                      <div className='flex justify-between items-center text-sm text-gray-500'>
                        <span>{session.messageCount} messages</span>
                        <div className='flex items-center gap-1'>
                          <Clock className='w-4 h-4' />
                          <span>
                            {new Date(session.createdAt).toLocaleDateString()}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}

                  {filteredSessions.length === 0 && (
                    <div className='text-center py-8 text-gray-500'>
                      No chat sessions found for this user.
                    </div>
                  )}
                </div>
              )
            ) : (
              <div className='text-center py-12 text-gray-500'>
                <MessageCircle className='w-12 h-12 mx-auto mb-4 text-gray-400' />
                <p>Select an external user to view their chat sessions</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Messages View */}
        <Card>
          <CardHeader>
            <CardTitle className='flex items-center gap-2'>
              <MessageCircle className='w-5 h-5' />
              Messages
              {selectedSession && (
                <span className='text-sm font-normal text-gray-500'>
                  - {selectedSession.title || 'Untitled Session'}
                </span>
              )}
            </CardTitle>
          </CardHeader>

          <CardContent>
            {selectedSession ? (
              <div className='space-y-4 max-h-96 overflow-y-auto'>
                {messages.map(message => (
                  <div
                    key={message.id}
                    className={`p-3 rounded-lg max-w-[85%] ${
                      message.role === 'user'
                        ? 'bg-blue-100 ml-auto'
                        : 'bg-gray-100'
                    }`}
                  >
                    <div className='flex justify-between items-start mb-1'>
                      <span className='font-medium text-sm'>
                        {message.role === 'user' ? 'User' : 'Assistant'}
                      </span>
                      <span className='text-xs text-gray-500'>
                        {new Date(message.createdAt).toLocaleTimeString()}
                      </span>
                    </div>
                    <p className='text-sm text-gray-700'>{message.content}</p>
                  </div>
                ))}

                {messages.length === 0 && (
                  <div className='text-center py-8 text-gray-500'>
                    No messages in this session.
                  </div>
                )}
              </div>
            ) : (
              <div className='text-center py-12 text-gray-500'>
                <MessageCircle className='w-12 h-12 mx-auto mb-4 text-gray-400' />
                <p>Select a chat session to view messages</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ChatHistory;
